import moment from "moment-timezone";
import { i18n, keycloak, validTlds } from "../main.ts";
import {
    AssociatedAccount,
    ClientInfo,
    Distributor,
    Language,
    Recipients,
    Trainer
} from "../types/PersologOnlineTypes.ts";
import { ProfileAdministrationAPI, TrainerAdministrationAPI } from "../api";
import axios from "axios";
import platform from "platform";
import { usePreferencesStore } from "../stores/preferences";
// Framework-agnostic helpers now live in @persolog/utils; re-export them so the
// existing `import { ... } from "../utils/GeneralUtils"` call sites keep working.
import { getCurrentTimeZone, getDisplayName } from "@persolog/utils";
export * from "@persolog/utils";

async function recordBrowserAnalytics() {
    if (localStorage.getItem("analytics") === "false") {
        return;
    }
    const clientInfo: ClientInfo = {
        browserName: platform.name ?? "Unknown",
        browserVersion: platform.version ?? "Unknown",
        osName: platform.os?.family ?? "Unknown",
        osVersion: platform.os?.version ?? "Unknown"
    };
    try {
        await ProfileAdministrationAPI.recordBrowserAnalytics(clientInfo);
        localStorage.setItem("analytics", "false");
    } catch (error) {
        console.warn("Error recording browser analytics:", error);
    }
}

/**
 * Tries to fetch trainer details. If it does not find one, then a new trainer is created.
 * Tries to fetch distributor from trainer administration and links it directly to new trainer.
 */
export async function getTrainerDetails(): Promise<Trainer | null> {
    await recordBrowserAnalytics();
    const impersonatedAccount = getImpersonatedAccount();
    if (impersonatedAccount) {
        return await ProfileAdministrationAPI.fetchTrainerDetails(impersonatedAccount.associatedAccount.id);
    }
    try {
        const trainerAccount = await ProfileAdministrationAPI.fetchTrainerDetails(keycloak.tokenParsed?.sub as string);
        if (trainerAccount.email.trim() !== keycloak.tokenParsed?.email?.trim() ||
            trainerAccount.firstname !== keycloak.tokenParsed?.given_name ||
            trainerAccount.lastname !== keycloak.tokenParsed?.family_name) {
            localStorage.setItem("newAccount", "false");
            return await ProfileAdministrationAPI.updateTrainerDetails(
                {
                    id: trainerAccount.id,
                    firstname: keycloak.tokenParsed?.given_name as string,
                    lastname: keycloak.tokenParsed?.family_name as string,
                    email: keycloak.tokenParsed?.email as string,
                    language: trainerAccount.language,
                    company: trainerAccount.company,
                    recipient: trainerAccount.recipient,
                    purchaseLimit: trainerAccount.purchaseLimit,
                    isDistributorAccount: trainerAccount.isDistributorAccount,
                    distributor: trainerAccount.distributor,
                    address: trainerAccount.address,
                    timeZone: trainerAccount.timeZone,
                    isStockKeepingDistributor: trainerAccount.isStockKeepingDistributor,
                    portrait: trainerAccount.portrait,
                    logo: trainerAccount.logo,
                    beta: trainerAccount.beta,
                    standardRetentionPeriod: trainerAccount.standardRetentionPeriod,
                    deletionNotification: trainerAccount.deletionNotification,
                    // carried through explicitly: the full PUT overwrites the row, so omitting this
                    // would reset the release-notes marker on every name sync
                    lastSeenReleaseNotesAt: trainerAccount.lastSeenReleaseNotesAt
                } as Trainer
            );
        }
        localStorage.setItem("newAccount", "false");
        return trainerAccount;
    } catch (e) {
        if (axios.isAxiosError(e) && e.request.status === 404 && keycloak.tokenParsed?.email) {
            localStorage.setItem("newAccount", keycloak.tokenParsed?.sub as string);
            const distributor = await getDistributorForTrainer(keycloak.tokenParsed?.email as string);

            //create new trainer ; create trainer
            return await ProfileAdministrationAPI.createTrainer({
                id: "0",
                firstname: keycloak.tokenParsed?.given_name as string,
                lastname: keycloak.tokenParsed?.family_name as string,
                email: keycloak.tokenParsed?.email as string,
                language: i18n.global.locale.value,
                company: "",
                recipient: Recipients.TRAINER,
                //We send 0 and let the backend decide the purchase limit based on the distributor's settings
                purchaseLimit: 0,
                isDistributorAccount: false,
                distributor: distributor,
                address: null,
                timeZone: getCurrentTimeZone(),
                isStockKeepingDistributor: false,
                portrait: "",
                logo: "",
                beta: true,
                standardRetentionPeriod: "P24M",
                deletionNotification: true
            });
        }
        throw e;
    }
}

/**
 * Fetches the distributor for the given trainer from the TrainerAdministrationAPI.
 */
export async function getDistributorForTrainer(trainerEmail: string): Promise<Distributor> {
    try {
        const distributorName = await TrainerAdministrationAPI.getDistributorNameForTrainer(trainerEmail);
        const distributors = await ProfileAdministrationAPI.fetchDistributors();
        const normalizedName = distributorName?.trim().toLowerCase();
        const distributor = distributors.find((d) => d.company.trim().toLowerCase() === normalizedName);

        if (distributor) {
            return distributor;
        } else {
            console.error("Distributor not found for trainer:", trainerEmail, "| distributorName returned:", distributorName, "| available:", distributors.map((d) => d.company));
            return {
                id: 1,
                company: "persolog GmbH",
                monthlyBillingEnabled: false
            } as Distributor;
        }
    } catch (error) {
        console.error("Error fetching distributor for trainer:", trainerEmail, error);
        return {
            id: 1,
            company: "persolog GmbH",
            monthlyBillingEnabled: false
        } as Distributor;
    }
}

/**
 * Deletes trainer from database.
 */
export async function hardDeleteTrainer(trainerId: string) {
    await ProfileAdministrationAPI.hardDeleteTrainer(trainerId)
        .then(() => {
            console.log("Trainer deleted successfully: ", trainerId);
        })
        .catch((error) => {
            if (axios.isAxiosError(error) && error.response?.status === 404) {
                console.warn("Trainer not found:", trainerId);
            }
        });
}

export async function initializeTlds() {
    try {
        const res = await fetch("https://data.iana.org/TLD/tlds-alpha-by-domain.txt");
        if (!res.ok) throw new Error(res.statusText);

        const text = await res.text();
        validTlds.value = new Set(
            text
                .split("\n")
                .filter((line) => line && !line.startsWith("#"))
                .map((tld) => tld.toLowerCase())
        );
    } catch (error) {
        console.error("Failed to fetch TLDs:", error);
        validTlds.value = new Set();
    }
}

function getTld(email: string): string | null {
    const match = new RegExp(/\.(\w+)$/).exec(email)
    return match ? match[1] : null;
}

export function isValidEmail(email: string): boolean {
    if (email.includes("mailto:")) return false

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) return false;

    const tld = getTld(email);

    // Only check TLDs if we have a non-empty set
    if (validTlds.value && validTlds.value.size > 0) {
        if (tld === null) return false;
        return validTlds.value.has(tld.toLowerCase());
    }
    // If no TLDs loaded, just rely on regex
    return true;
}

export function getAllTimeZones() {
    return moment.tz.names();
}

export function getLanguage(language: string | undefined): string | undefined {
    return getDisplayName(i18n.global.locale.value, language ?? "");
}

export function getBrowserLanguage() {
    const language = navigator.language.split("-")[0];
    const supportedLanguages: string[] = i18n.global.availableLocales;
    const defaultLanguage = i18n.global.locale.value;

    return language && supportedLanguages.includes(language) ? language : defaultLanguage;
}

/**
 * Changes the language of the application.
 */
export const changeLanguage = (lang: Language) => {
    const preferencesStore = usePreferencesStore();
    preferencesStore.setLanguage(lang);
    // vue-i18n types `locale` as a union derived from the message schema; `lang` is a valid locale.
    i18n.global.locale.value = lang as typeof i18n.global.locale.value;
};

/**
 * Returns the name of the recipient.
 * In case of trainer, the trainer's name otherwise the recipient configuration name.
 */
export function getRecipientName(trainer: Trainer, recipient: string) {
    if (recipient === Recipients.TRAINER) {
        return `${trainer.firstname} ${trainer.lastname} (${i18n.global.t("common.you")})`;
    } else {
        return i18n.global.t("project-configuration.recipients." + recipient.toLowerCase());
    }
}

export function getImpersonatedAccount(): AssociatedAccount | null {
    const accountStr = localStorage.getItem("impersonatedAccount");
    if (accountStr) {
        return JSON.parse(accountStr);
    } else {
        return null;
    }
}

export function isNewAccount(): boolean {
    return (
        localStorage.getItem("newAccount") !== null &&
        localStorage.getItem("newAccount") !== "undefined" &&
        localStorage.getItem("newAccount") !== "false" &&
        localStorage.getItem("newAccount") !== undefined
    );
}

export const patternOrder = [
    "1",
    "12",
    "13",
    "14",
    "124",
    "2",
    "21",
    "23",
    "24",
    "123",
    "3",
    "31",
    "32",
    "34",
    "234",
    "4",
    "41",
    "42",
    "43",
    "134"
];
